import styled from 'styled-components';

import { colors } from 'constants/colors';
import { DeliveryState, TaskState } from 'data/__generated__';

export type StateTone = 'neutral' | 'info' | 'success' | 'danger' | 'warning' | 'task' | 'taskDone';

// A task never passes for a delivery: violet while to do, another green once done.
export const TASK_COLOR = colors.violet;
export const TASK_DONE_COLOR = '#166534';
const TASK_DONE_DARK_THEME_COLOR = '#4ade80';

/**
 * The one place a delivery state becomes a colour. Shared by the status pill (tour cards) and the timeline
 * chips, so « vert = livré » means the same thing wherever the dispatcher looks.
 */
export const toneColor = (
    theme: { infoTextColor: string; errorColor: string; warningColor: string },
    tone: StateTone,
): string =>
    ({
        neutral: theme.infoTextColor,
        info: '#2563eb',
        success: '#16a34a',
        danger: theme.errorColor,
        warning: theme.warningColor,
        task: TASK_COLOR,
        taskDone: TASK_DONE_COLOR,
    })[tone];

export const STATE_TONE: Partial<Record<DeliveryState, StateTone>> = {
    [DeliveryState.Created]: 'info',
    [DeliveryState.Packaging]: 'info',
    [DeliveryState.Ready]: 'info',
    [DeliveryState.InProgress]: 'info',
    [DeliveryState.Delivered]: 'success',
    [DeliveryState.NotDelivered]: 'danger',
    [DeliveryState.Aborted]: 'danger',
    [DeliveryState.Paused]: 'warning',
};

export const TASK_STATE_TONE: Record<TaskState, StateTone> = {
    [TaskState.Created]: 'task',
    [TaskState.InProgress]: 'task',
    [TaskState.Finished]: 'taskDone',
    [TaskState.Failed]: 'danger',
    [TaskState.Aborted]: 'danger',
};

export const StatePill = styled.span<{ $tone: StateTone }>`
    flex-shrink: 0;
    font-size: 11px;
    font-weight: 700;
    padding: 3px 9px;
    border-radius: 999px;
    white-space: nowrap;
    ${({ theme, $tone }) => {
        const color = toneColor(theme, $tone);
        return `color: ${color}; background: color-mix(in srgb, ${color} 13%, transparent);`;
    }}

    ${({ $tone }) =>
        $tone === 'taskDone' &&
        `.dark & { color: ${TASK_DONE_DARK_THEME_COLOR}; background: color-mix(in srgb, ${TASK_DONE_DARK_THEME_COLOR} 13%, transparent); }`}
`;

/** A stop's delivery window (« 07:30–10:30 »), muted when free. Shared by the tour cards and the « À affecter » pool. */
export const WindowChip = styled.span<{ $muted: boolean }>`
    flex-shrink: 0;
    font-size: 11px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    padding: 3px 4px;
    border-radius: 7px;
    white-space: nowrap;
    background: ${({ theme }) => theme.cardBackgroundColor};
    border: 1px solid ${({ theme }) => theme.lightBorderColor};
    color: ${({ $muted, theme }) => ($muted ? theme.infoTextColor : theme.textColor)};
`;
